import React, { useState, useEffect } from "react";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import Icon from "@material-ui/core/Icon";
import { makeStyles } from "@material-ui/core";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import HistoryIcon from "@material-ui/icons/History";
import Tooltip from "@material-ui/core/Tooltip";

// Local storage key for search history
const SEARCH_HISTORY_KEY = "album_search_history";

// Maximum number of history items to store
const MAX_HISTORY_ITEMS = 10;

const useStyles = makeStyles((theme) => ({
  base: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    position: "relative",
  },
  textbox: {
    width: "100%",
    flexGrow: 1,
    "& .MuiOutlinedInput-root": {
      // Always show a visible outline
      "& fieldset": {
        borderWidth: "1px",
        borderStyle: "solid",
        // Use a semi-transparent white border that works on most backgrounds
        borderColor: "rgba(255, 255, 255, 0.2)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(255, 255, 255, 0.2)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "rgba(255, 255, 255, 0.2)",
      },
    },
    // Style the placeholder text
    "& .MuiInputBase-input::placeholder": {
      opacity: 0.7,
      // Make sure the placeholder is visible enough
      color: "inherit",
    },
  },
  clear: {
    position: "absolute",
    right: 0,
    top: "50%",
    transform: "translateY(-50%)",
    marginRight: theme.spacing(1),
    zIndex: 10,
  },
  historyIcon: {
    position: "absolute",
    right: theme.spacing(5),
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 10,
    color: "rgba(255, 255, 255, 0.5)",
    "&:hover": {
      color: "rgba(255, 255, 255, 0.8)",
    },
  },
  inputWithClearButton: {
    paddingRight: theme.spacing(5),
  },
  historyMenu: {
    "& .MuiMenu-paper": {
      maxHeight: "80vh", // 80% of viewport height
      overflowY: "auto",  // Enable scrolling if needed
      width: 300, // Set a fixed width or make it match the search input
      maxWidth: "90vw", // Ensure it doesn't overflow on small screens
      padding: theme.spacing(1, 0), // Add some padding
    },
    "& .MuiMenuItem-root": {
      minHeight: 40, // Ensure items have a reasonable height
      padding: theme.spacing(1, 2), // Add some padding to menu items
    }
  },
  noHistoryText: {
    padding: theme.spacing(1, 2),
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  },
}));

const AlbumSearch = (props) => {
  const { 
    handleChange, 
    handleSearch, 
    clearSearch, 
    value, 
    q, // Support both value and q prop names for backward compatibility
    placeholder = "Buscar...", // Default placeholder
    size = "medium", // Add size prop with default
    showClearButton = true, // Control clear button visibility
    className, // Allow additional class names
    inputProps = {}, // Allow additional input props
    customStyles = {} // Custom styles for colors
  } = props;
  
  const classes = useStyles();
  const searchValue = value !== undefined ? value : q; // Use value or q prop
  
  // State for search history
  const [searchHistory, setSearchHistory] = useState([]);
  const [historyAnchorEl, setHistoryAnchorEl] = useState(null);
  
  // Add this effect to trigger search when selecting from history
  const [shouldTriggerSearch, setShouldTriggerSearch] = useState(false);
  const [selectedHistoryTerm, setSelectedHistoryTerm] = useState('');
  
  // Load search history from localStorage on component mount
  useEffect(() => {
    const savedHistory = localStorage.getItem(SEARCH_HISTORY_KEY);
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        if (Array.isArray(parsedHistory)) {
          setSearchHistory(parsedHistory);
        }
      } catch (error) {
        console.error("Error parsing search history:", error);
      }
    }
  }, []);
  
  useEffect(() => {
    if (shouldTriggerSearch && selectedHistoryTerm) {
      handleSearch(selectedHistoryTerm);
      setShouldTriggerSearch(false);
      setSelectedHistoryTerm('');
    }
  }, [shouldTriggerSearch, selectedHistoryTerm, handleSearch]);
  
  // Extract custom style properties with defaults
  const {
    inputColor,
    borderColor,
    iconColor
  } = customStyles;

  // Custom border color if provided
  const borderStyles = borderColor ? {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: borderColor,
      },
      "&:hover fieldset": {
        borderColor: borderColor,
      },
      "&.Mui-focused fieldset": {
        borderColor: borderColor,
      },
    }
  } : {};

  // Handle change with trimming
  const handleChangeWithTrim = (e) => {
    const trimmedValue = e.target.value.trim();
    
    if (trimmedValue !== e.target.value) {
      const newEvent = {
        ...e,
        target: {
          ...e.target,
          value: trimmedValue
        }
      };
      handleChange(newEvent);
    } else {
      handleChange(e);
    }
  };

  // Handle key press events
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      const trimmedValue = searchValue?.trim() || '';
      
      if (trimmedValue) {
        addToSearchHistory(trimmedValue);
        handleSearch(trimmedValue);
      }
      
      e.preventDefault();
    }
  };
  
  // Add a search term to history
  const addToSearchHistory = (term) => {
    if (!term || searchHistory.includes(term)) return;
    
    const newHistory = [
      term,
      ...searchHistory.filter(item => item !== term)
    ].slice(0, MAX_HISTORY_ITEMS);
    
    setSearchHistory(newHistory);
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(newHistory));
  };
  
  // Clear all search history
  const clearSearchHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem(SEARCH_HISTORY_KEY);
    setHistoryAnchorEl(null);
  };
  
  // Handle history icon click
  const handleHistoryClick = (event) => {
    setHistoryAnchorEl(event.currentTarget);
  };
  
  // Handle history menu close
  const handleHistoryClose = () => {
    setHistoryAnchorEl(null);
  };
  
  // Handle selecting a history item
  const handleHistoryItemClick = (term) => {
    // Close the menu
    setHistoryAnchorEl(null);
    
    // Update the search input
    handleChange({
      target: {
        value: term,
        name: "q"
      }
    });
    
    // Set the flag and term to trigger the search in the effect
    setSelectedHistoryTerm(term);
    setShouldTriggerSearch(true);
  };

  return (
    <div className={`${classes.base} ${className || ''}`}>
      <TextField
        id="query"
        name="q"
        value={searchValue || ''}
        placeholder={placeholder}
        onChange={handleChangeWithTrim}
        onKeyPress={handleKeyPress}
        fullWidth
        variant="outlined"
        size={size}
        className={classes.textbox}
        InputProps={{
          ...inputProps,
          style: {
            color: inputColor || 'inherit',
            ...(inputProps.style || {}),
          },
          classes: {
            root: searchValue ? classes.inputWithClearButton : '',
          }
        }}
        label=""
        style={borderStyles}
      />
      
      {/* History button - only show if we have history */}
      {searchHistory.length > 0 && (
        <Tooltip title="Historial de búsqueda">
          <IconButton
            size="small"
            onClick={handleHistoryClick}
            className={classes.historyIcon}
            style={{ color: iconColor || 'inherit' }}
            aria-label="Search history"
          >
            <HistoryIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      
      {/* History menu */}
      <Menu
        anchorEl={historyAnchorEl}
        keepMounted
        open={Boolean(historyAnchorEl)}
        onClose={handleHistoryClose}
        className={classes.historyMenu}
      >
        {searchHistory.length > 0 ? (
          // Use an array instead of a fragment
          [
            // Map history items
            ...searchHistory.map((term, index) => (
              <MenuItem key={`history-${index}`} onClick={() => handleHistoryItemClick(term)}>
                {term}
              </MenuItem>
            )),
            // Add clear history item
            <MenuItem key="clear-history" onClick={clearSearchHistory}>
              <span style={{ color: 'red' }}>Borrar historial</span>
            </MenuItem>
          ]
        ) : (
          <MenuItem disabled>No hay historial de búsqueda</MenuItem>
        )}
      </Menu>
      
      {/* Clear button */}
      {(showClearButton && searchValue) && (
        <IconButton 
          size="small" 
          onClick={clearSearch} 
          className={classes.clear}
          style={{ color: iconColor || 'inherit' }}
          aria-label="Clear search"
        >
          <Icon>clear</Icon>
        </IconButton>
      )}
    </div>
  );
};

export default AlbumSearch;
